import { observer } from 'mobx-react-lite'
import i from '../../../../translations/i'
import { Titles } from '../../../../layouts/Titles'
import { Button, Input, InputNumber, Modal, Space, Table, Popconfirm, Switch, Badge, Row, Col, Divider, Tag, Tooltip } from 'antd'
import { DeleteOutlined, EditOutlined, CarOutlined, PlusOutlined } from '@ant-design/icons'
import React, { useCallback, useEffect, useRef } from 'react'
import { FullscreenControl, Map, Polygon, YMaps, ZoomControl } from '@pbe/react-yandex-maps'
import { zonesController, PriceLevel, DEFAULT_ZONE_COLOR, MapViewport } from './zones.controller'
import { returnParseProjectElement } from '../../../../helpers/project.controller'
import { GoogleMapZone } from '../../../../components/map/google/GoogleMapZone'
import { SectionBadge, PlainField } from '../../../../components/SectionCard'

const controller = new zonesController()

// Сортировка ступеней по возрастанию суммы, ступень без верхней границы ("от X ₽") — всегда последняя.
const sortPriceLevels = (levels: PriceLevel[]): PriceLevel[] =>
    [...levels].sort((a, b) => {
        if (a.max_cart === null) return 1
        if (b.max_cart === null) return -1
        return a.max_cart - b.max_cart
    })

const applyYandexViewport = (map: any, viewport: MapViewport | null) => {
    if (!map || !viewport) return

    if (viewport.bounds) {
        map.setBounds(viewport.bounds, { checkZoomRange: true, zoomMargin: 40 })
    } else if (viewport.center) {
        map.setCenter(viewport.center, viewport.zoom ?? map.getZoom())
    }
}

const syncYandexView = (map: any) => {
    controller.setMapView(map.getCenter(), map.getZoom(), map.getBounds())
}

const priceLevelLabel = (level: PriceLevel, sorted: PriceLevel[], index: number): string =>
    level.max_cart === null
        ? i.t('delivery:zones.priceLevelPreviewFrom', { sum: sorted[index - 1]?.max_cart ?? 0 })
        : i.t('delivery:zones.priceLevelPreviewUpTo', { sum: level.max_cart })

export const SettingDeliveryZones = observer(() => {

    const draw = (ref: any) => {
        ref.editor.startEditing()
    }

    const columns = [
        {
            title: '',
            dataIndex: 'enabled',
            key: 'enabled',
            render: (active: boolean) => (
                <Badge
                    status={active ? 'success' : 'error'}
                />
            )
        },
        { title: i.t('delivery:zones.name'), dataIndex: 'name', key: 'name' },
        {
            title: i.t('delivery:zones.color'),
            dataIndex: 'color',
            key: 'color',
            render: (value: string | null) => (
                <span style={{
                    display: 'inline-block',
                    width: 18,
                    height: 18,
                    borderRadius: 4,
                    border: '1px solid #e8e8ef',
                    background: value || DEFAULT_ZONE_COLOR
                }} />
            )
        },
        {
            title: i.t('delivery:zones.price'),
            key: 'price',
            render: (_: any, record: any) => {
                const levels: PriceLevel[] = record.price_levels ?? []

                if (levels.length === 0) {
                    return `${record.price ?? 0} ₽`
                }

                const sorted = sortPriceLevels(levels)
                const tooltip = sorted.map((level, index) => `${priceLevelLabel(level, sorted, index)} — ${level.price} ₽`).join('\n')

                return (
                    <Tooltip title={<div style={{ whiteSpace: 'pre-line' }}>{tooltip}</div>}>
                        <Tag color="purple">{i.t('delivery:zones.levelsCount', { count: levels.length })}</Tag>
                    </Tooltip>
                )
            }
        },
        { title: i.t('delivery:zones.minPrice'), dataIndex: 'minprice', key: 'minprice', },
        { title: i.t('delivery:zones.maxPrice'), dataIndex: 'maxprice', key: 'maxprice', },
        {
            title: i.t('delivery:zones.waitingTime'),
            dataIndex: 'waiting_time',
            key: 'waiting_time',
            render: (value: string | null) => value || '—'
        },
        {
            title: i.t('delivery:zones.deliveryTime'),
            dataIndex: 'delivery_time',
            key: 'delivery_time',
            render: (value: number | null) => value ? i.t('delivery:zones.deliveryTimeValue', { minutes: value }) : '—'
        },
        {
            title: i.t('delivery:zones.actions'),
            key: 'action',
            render: (text: any, record: any) => (
                <Space size="middle">
                    <Button
                        type="primary"
                        shape="circle"
                        icon={<EditOutlined />}
                        onClick={() => controller.edit(record.id)}
                    />
                    <Popconfirm
                        title={i.t('def:confirms.delete')}
                        onConfirm={() => controller.deleteByID(record.id)}
                        // onCancel={cancel}
                        okText={i.t('def:yes')}
                        cancelText={i.t('def:no')}
                    >
                        <Button
                            type="dashed"
                            shape="circle"
                            icon={<DeleteOutlined />}
                            danger
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ]

    const zoneOptions = (color: string | null, draggable: boolean) => ({
        fillColor: color || DEFAULT_ZONE_COLOR,
        strokeColor: color || DEFAULT_ZONE_COLOR,
        fillOpacity: 0.3,
        strokeWidth: 3,
        draggable
    })

    const mapBlockRef = useRef<HTMLDivElement>(null)
    const yandexMapInstanceRef = useRef<any>(null)

    useEffect(() => {
        controller.getAllZone(true)
    }, [])

    const viewport = controller.viewport

    useEffect(() => {
        applyYandexViewport(yandexMapInstanceRef.current, viewport)
    }, [viewport])

    const onYandexMapRef = useCallback((map: any) => {
        yandexMapInstanceRef.current = map
        if (!map) return

        syncYandexView(map)
        // Зоны/геолокация могли прийти раньше, чем карта успела создаться.
        applyYandexViewport(map, controller.viewport)
    }, [])

    // Нативный CSS-ресайз меняет размер контейнера, но карта об этом не узнаёт сама —
    // явно просим её пересчитать размер при каждом изменении высоты блока.
    useEffect(() => {
        if (!mapBlockRef.current || typeof ResizeObserver === 'undefined') {
            return
        }

        const observer = new ResizeObserver(() => {
            yandexMapInstanceRef.current?.container?.fitToViewport()
        })

        observer.observe(mapBlockRef.current)

        return () => observer.disconnect()
    }, [])

    return(
        <>
            <Titles
                title={i.t('settings:delivery.zones.title')}
            />

            <div
                ref={mapBlockRef}
                className="ymap_block"
                style={{
                    marginTop: 16,
                    marginBottom: 24,
                    height: 400,
                    minHeight: 260,
                    maxHeight: '85vh',
                    resize: 'vertical',
                    overflow: 'auto',
                    border: '1px solid #e8e8ef',
                    borderRadius: 12
                }}
            >
                {returnParseProjectElement('typemap') === 'yandex'
                    ? <>
                        <YMaps
                            version={'2.1'}
                            query={{
                                apikey: "50d6500a-1178-4721-9006-de55e27a6dc2",
                            }}
                        >
                            <div className="zone">
                                <div className="buttons">
                                    <Space>
                                        <Button
                                            type="primary"
                                            size="small"
                                            onClick={() => controller.openModal()}
                                            disabled={controller.active.id > -1}
                                        >
                                            {i.t('delivery:zones.create')}
                                        </Button>
                                        <Button
                                            type="primary"
                                            size="small"
                                            danger
                                            onClick={() => controller.delete()}
                                            disabled={controller.active.id < 0}
                                        >
                                            {i.t('delivery:zones.delete')}
                                        </Button>
                                        <Button
                                            type="primary"
                                            size="small"
                                            onClick={() => controller.saveActive()}
                                            disabled={controller.active.id < 0}
                                        >
                                            {i.t('delivery:zones.save')}
                                        </Button>
                                        <Button
                                          type="dashed"
                                          size="small"
                                          onClick={() => controller.edit(controller.active.id)}
                                          disabled={controller.active.id < 0}
                                        >
                                            {i.t('delivery:zoneSettings')}
                                        </Button>
                                    </Space>
                                </div>
                                <Map
                                    instanceRef={onYandexMapRef}
                                    defaultState={{ center: controller.mapView.center, zoom: controller.mapView.zoom }}
                                    width={100 + "%"}
                                    height={100 + "%"}
                                    modules={["geoObject.addon.editor"]}
                                    onBoundsChange={(event: any) => syncYandexView(event.get('target'))}
                                >
                                    <FullscreenControl />
                                    <ZoomControl />
                                    {controller.active.id > -1 &&
                                        <Polygon
                                            geometry={[controller.active.coordinates]}
                                            onGeometryChange={(e: any) => controller.onChange(e)}
                                            instanceRef={(ref: any) => ref && draw(ref)}
                                            options={zoneOptions(controller.active.color, true)}
                                        ></Polygon>
                                    }
                                    {controller.list.map(item =>
                                        <>
                                            {controller.active.id !== item.id &&
                                                <Polygon
                                                    geometry={[item.coordinates]}
                                                    onClick={() => controller.select(item.id)}
                                                    options={zoneOptions(item.color, false)}
                                                />
                                            }
                                        </>
                                    )}
                                </Map>
                            </div>
                        </YMaps>
                        </>
                    : <>
                        <div
                            style={{ width: '100%', height: '100%', position: 'relative' }}
                        >
                            <div className="buttons">
                                <Space>
                                    <Button
                                        type="primary"
                                        size="small"
                                        onClick={() => controller.openModal()}
                                        disabled={controller.active.id > -1}
                                    >
                                        {i.t('delivery:zones.create')}
                                    </Button>
                                    <Button
                                        type="primary"
                                        size="small"
                                        danger
                                        onClick={() => controller.delete()}
                                        disabled={controller.active.id < 0}
                                    >
                                        {i.t('delivery:zones.delete')}
                                    </Button>
                                    <Button
                                        type="primary"
                                        size="small"
                                        onClick={() => controller.saveActive()}
                                        disabled={controller.active.id < 0}
                                    >
                                        {i.t('delivery:zones.save')}
                                    </Button>
                                </Space>
                            </div>
                            <GoogleMapZone
                                controller={controller}
                                coordinates={controller.active.coordinatesGoogle}
                            />
                        </div>
                    </>
                }
            </div>


            <Table columns={columns} dataSource={controller.list} rowKey={'id'} />

            <Modal
                visible={controller.modal}
                title={null}
                onCancel={() => controller.closeModal()}
                footer={null}
                centered={true}
                maskClosable={false}
                width={640}
            >
                <form onSubmit={e => controller.saveDataZone(e)} style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 28 }}>
                    <SectionBadge
                        icon={<CarOutlined />}
                        text={controller.active.name || i.t('delivery:zones.edit')}
                        background="linear-gradient(135deg, var(--primary-color), #9186e0)"
                        toggle={
                            <Switch
                                checked={controller.active.enabled}
                                onChange={e => controller.onChangeInput('enabled', e)}
                                checkedChildren={i.t('def:on')}
                                unCheckedChildren={i.t('def:off')}
                            />
                        }
                    />

                    <PlainField label={i.t('delivery:zones.nameZone')}>
                        <Input
                            value={controller.active.name}
                            onChange={e => controller.onChangeInput("name", e.target.value)}
                            placeholder={i.t('delivery:zones.nameZone')}
                            required
                        />
                    </PlainField>

                    <PlainField label={i.t('def:id_crm')}>
                        <Input
                            value={controller.active.id_crm ?? ''}
                            onChange={e => controller.onChangeInput("id_crm", e.target.value)}
                            placeholder={i.t('def:id_crm')}
                        />
                    </PlainField>

                    <Row gutter={[16, 16]} style={{ width: '100%', margin: 0 }}>
                        <Col span={12} style={{ paddingLeft: 0 }}>
                            <PlainField label={i.t('delivery:zones.minPrice')}>
                                <InputNumber
                                    style={{ width: '100%' }}
                                    value={controller.active.minprice ?? 0}
                                    onChange={e => controller.onChangeInput("minprice", e)}
                                    min={0}
                                />
                            </PlainField>
                        </Col>
                        <Col span={12} style={{ paddingRight: 0 }}>
                            <PlainField label={i.t('delivery:zones.maxPrice')}>
                                <InputNumber
                                    style={{ width: '100%' }}
                                    value={controller.active.maxprice ?? 0}
                                    onChange={e => controller.onChangeInput("maxprice", e)}
                                    min={0}
                                />
                            </PlainField>
                        </Col>
                    </Row>

                    <PlainField label={i.t('delivery:zones.price')}>
                        <InputNumber
                            style={{ width: '100%' }}
                            value={controller.active.price ?? 0}
                            onChange={e => controller.onChangeInput("price", e)}
                            min={0}
                        />
                    </PlainField>

                    <Divider dashed style={{ margin: 0 }} />

                    <Row gutter={[16, 16]} style={{ width: '100%', margin: 0 }}>
                        <Col span={12} style={{ paddingLeft: 0 }}>
                            <PlainField label={i.t('delivery:zones.waitingTime')}>
                                <Input
                                    value={controller.active.waiting_time ?? ''}
                                    onChange={e => controller.onChangeInput('waiting_time', e.target.value)}
                                    placeholder={i.t('delivery:zones.waitingTimePlaceholder')}
                                />
                            </PlainField>
                        </Col>
                        <Col span={12} style={{ paddingRight: 0 }}>
                            <PlainField label={i.t('delivery:zones.deliveryTime')}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        value={controller.active.delivery_time}
                                        onChange={e => controller.onChangeInput('delivery_time', e || null)}
                                        min={0}
                                        precision={0}
                                        placeholder={i.t('delivery:zones.deliveryTimePlaceholder')}
                                    />
                                    <span style={{ fontSize: 12, color: 'var(--text-muted-color)' }}>{i.t('delivery:zones.deliveryTimeHint')}</span>
                                </div>
                            </PlainField>
                        </Col>
                    </Row>

                    <PlainField label={i.t('delivery:zones.color')}>
                        <Input
                            type="color"
                            style={{ width: 64, height: 32, padding: 2 }}
                            value={controller.active.color ?? DEFAULT_ZONE_COLOR}
                            onChange={e => controller.onChangeInput('color', e.target.value)}
                        />
                    </PlainField>

                    <Divider dashed style={{ margin: 0 }} />

                    <PriceLevelsEditor />

                    <Space>
                        <Button
                            htmlType="submit"
                        >
                            {i.t('delivery:zones.map')}
                        </Button>
                        {controller.active.id > 0 &&
                            <Button type="primary" onClick={() => controller.saveActive()}>{i.t('delivery:save')}</Button>
                        }
                    </Space>
                </form>
            </Modal>
        </>
    )
})

// Редактор ступеней "стоимость доставки по сумме корзины" — как в примере с "Доставка при
// заказе до 1500 ₽ — 500 ₽" / "от 3000 ₽ — 300 ₽". max_cart = null означает верхнюю
// ("от ...") ступень без ограничения сверху.
const PriceLevelsEditor = observer(() => {
    const levels = controller.active.price_levels

    return (
        <PlainField label={i.t('delivery:zones.priceLevelsTitle')}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
                {levels.length === 0 &&
                    <span style={{ fontSize: 12, color: 'var(--text-muted-color)' }}>{i.t('delivery:zones.priceLevelsEmpty')}</span>
                }

                {levels.map((level, index) => (
                    <div
                        key={`price_level_${index}`}
                        style={{
                            display: 'flex', alignItems: 'flex-end', gap: 10,
                            background: 'var(--surface-color)', border: '1px solid var(--border-light-color)', borderRadius: 10,
                            padding: '10px 12px'
                        }}
                    >
                        <span style={{
                            width: 24, height: 24, borderRadius: 8, flexShrink: 0, marginBottom: 4,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: 'rgba(114,100,214,0.12)', color: 'var(--primary-color)',
                            fontSize: 12, fontWeight: 700
                        }}>
                            {index + 1}
                        </span>

                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 11, color: 'var(--text-muted-color)', marginBottom: 4 }}>
                                {i.t('delivery:zones.priceLevelMaxCart')}
                            </div>
                            <InputNumber
                                style={{ width: '100%' }}
                                min={0}
                                placeholder={i.t('delivery:zones.priceLevelUnbounded')}
                                value={level.max_cart}
                                onChange={(e) => controller.updatePriceLevel(index, 'max_cart', e)}
                            />
                        </div>

                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 11, color: 'var(--text-muted-color)', marginBottom: 4 }}>
                                {i.t('delivery:zones.priceLevelPrice')}
                            </div>
                            <InputNumber
                                style={{ width: '100%' }}
                                min={0}
                                value={level.price}
                                onChange={(e) => controller.updatePriceLevel(index, 'price', e)}
                            />
                        </div>

                        <Button
                            type="text"
                            danger
                            shape="circle"
                            icon={<DeleteOutlined />}
                            onClick={() => controller.removePriceLevel(index)}
                        />
                    </div>
                ))}

                <Button type="dashed" block icon={<PlusOutlined />} onClick={() => controller.addPriceLevel()}>
                    {i.t('delivery:zones.addPriceLevel')}
                </Button>

                {levels.length > 0 &&
                    <PriceLevelsPreview levels={levels} />
                }
            </div>
        </PlainField>
    )
})

// Живой предпросмотр ступеней в том же виде, в каком их увидит покупатель в приложении.
const PriceLevelsPreview = ({ levels }: { levels: PriceLevel[] }) => {
    const sorted = sortPriceLevels(levels.filter(level => level.price !== null && level.price !== undefined))

    if (sorted.length === 0) {
        return <></>
    }

    return (
        <div style={{
            background: 'var(--surface-field-color)', borderRadius: 10, padding: '10px 14px',
            display: 'flex', flexDirection: 'column', gap: 6
        }}>
            {sorted.map((level, index) => {
                const isUnbounded = level.max_cart === null
                const label = priceLevelLabel(level, sorted, index)

                return (
                    <div key={`preview_${index}`} style={{
                        display: 'flex', justifyContent: 'space-between', gap: 12,
                        fontSize: 13, fontWeight: isUnbounded ? 700 : 400,
                        color: 'var(--text-color)'
                    }}>
                        <span>{label}</span>
                        <span>{level.price} ₽</span>
                    </div>
                )
            })}
        </div>
    )
}